import sumBy from 'lodash/sumBy'
import { mapGetters, mapActions } from 'vuex'

import { effectiveLimit, overagePercentage } from 'app/utils/subscription'

const KITS_INFINITY = -1
const PAGEVIEWS_INFINITY = -1

// A threshold the progress bar can never cross, for the cases where there is no
// overage band to be in danger of using up. `dangerPercentage` is compared
// against a 0-100 progress value (`components-wa/usage/ProgressBar.vue`), so
// anything above 100 means "never".
const NEVER_DANGER_PERCENTAGE = 101

export const KitSubscriptionMixin = {
  data() {
    return {
      // TODO: Metered Billing full release: remove
      isMeteredBillingPageviewsPartiallyReleased: false
    }
  },

  computed: {
    ...mapGetters('meteredBilling', ['meteredBillingSettings']),

    canCreateProKits() {
      return this.$permits.get('accessToKits', 'proAllowed')
    },

    hasInfiniteKits() {
      return this.$permits.get('accessToKits', 'kitLimit') === KITS_INFINITY
    },

    hasPageviewsUnlimited() {
      return this.meteredBillingSettings?.maxMonthlyPageviews === PAGEVIEWS_INFINITY
    },

    hasSetPageviewsBillingLimit() {
      return this.meteredBillingSettings?.maxMonthlyPageviewsMoneyLimit > 0
    },

    isMeteredBillingPageviews() {
      return this.isMeteredBillingPageviewsFeatureOn && this.isMeteredBillingPageviewsCustomerEnabled
    },

    isMeteredBillingPageviewsCustomerEnabled() {
      return this.meteredBillingSettings?.meteredPageviewsOn === true
    },

    isMeteredBillingPageviewsFeatureOn() {
      return FONTAWESOME.METERED_PAGEVIEWS_ACTIVE === 'yes'
    },

    kitLimit() {
      return this.$permits.get('accessToKits', 'kitLimit') || 0
    },

    // The size of the overage band: how many pageviews past the pre-paid limit
    // the customer's metered-billing cap allows. `0` when metered billing is off
    // (no band at all) and `PAGEVIEWS_INFINITY` when they've set no cap.
    kitPageviewsOverageLimit() {
      if (!this.isMeteredBillingPageviewsCustomerEnabled) return 0

      if (this.hasPageviewsUnlimited) return PAGEVIEWS_INFINITY

      return this.totalPageviewsUsageLimit - this.pageviewsLimit
    },

    pageviewsDollarLimit() {
      return this.meteredBillingSettings?.maxMonthlyPageviewMoneyLimit > 0 ? this.meteredBillingSettings?.maxMonthlyPageviewsMoneyLimit / 100 : 0
    },

    // Pageviews the customer actually has: the plan's allowance plus any
    // purchased add-ons. Overage bills against this, so usage counters and
    // warning thresholds must measure against it too.
    pageviewsLimit() {
      return effectiveLimit(this.$subscription, 'monthlyPageviews') || 0
    },

    // The plan's own allowance, add-ons excluded — for copy that describes what
    // the tier includes ("Your Pro plan comes with N pageviews per month").
    pageviewsPlanLimit() {
      return this.$subscription?.plan?.monthlyPageviews || 0
    },

    planHasAccessToPageviewMeteredBilling() {
      return this.$subscription?.plan?.meteredBillingPageviewsEnabled
    },

    overagePageviewIncrement() {
      return this.$subscription?.plan?.overagePageviewIncrement || 0
    },

    totalPageviewsUsageLimit() {
      return this.meteredBillingSettings?.maxMonthlyPageviews
    }
  }
}

export const KitsStatusAndUsageMixin = {
  data() {
    return {
      hasFetchedKits: false,
      kitsFetchFailed: false
    }
  },

  computed: {
    ...mapGetters('kits', ['currentKits', 'activeKits']),

    ...KitSubscriptionMixin.computed,

    kitCount() {
      // pending kits don't count towards the kit count
      return this.activeKits.length
    },

    // A pending Kit never blocks a create: it doesn't count toward the limit
    // (`kitCount` is active Kits only), and a new pending Kit replaces it.
    doNotAllowAnyMoreKits() {
      return !this.hasInfiniteKits && this.kitCount >= this.kitLimit
    },

    kitsLoading() {
      return !this.hasFetchedKits
    },

    atPageviewsPlanLimit() {
      return this.totalPageviews >= this.pageviewsLimit
    },

    atPageviewsUsageLimit() {
      // if metered pageviews limit is set to none (-1) then user would not be at the limit so we return false
      if (this.meteredBillingSettings?.maxMonthlyPageviews === -1) return false

      return this.totalPageviews >= this.meteredBillingSettings?.maxMonthlyPageviews
    },

    totalPageviews() {
      return sumBy(this.currentKits, function (k) {
        return k.pageviews
      })
    },

    hasReachedPageviewsOverageLimit() {
      return this.isMeteredBillingPageviews && !this.hasPageviewsUnlimited && this.totalPageviews > this.totalPageviewsUsageLimit
    },

    kitUsagePercentage() {
      return (this.totalPageviews / this.pageviewsLimit) * 100
    },

    kitPageviewsOverageUsage() {
      const overage = this.totalPageviews - this.pageviewsLimit

      return Math.max(overage, 0)
    },

    kitPageviewsOverageUsagePercentage() {
      return overagePercentage(this.kitPageviewsOverageUsage, this.kitPageviewsOverageLimit)
    },

    // Danger colour once the overage band is used up — but only when there is a
    // band to use up. The exact shape (and the exact defect) as
    // `bandwidthOverageDangerPercentage`: `kitPageviewsOverageLimit` returns
    // `PAGEVIEWS_INFINITY` for an uncapped band and can be `0` or negative
    // otherwise, all of which a bare truthiness test read as a real band.
    kitPageviewsOverageDangerPercentage() {
      return this.kitPageviewsOverageLimit > 0 ? 100 : NEVER_DANGER_PERCENTAGE
    },

    kitsDeactivated() {
      return this.currentKits.some((k) => k.status === 'deactivated')
    },

    billablePageviewsOverage() {
      if (this.$subscription?.type === 'free') return false

      const actualOveragePageviews = Math.max(this.totalPageviews - this.pageviewsLimit, 0)

      const overagePageviewIncrement = this.$subscription?.plan?.overagePageviewIncrement || 0

      const isOverageLessThanOneIncrement = actualOveragePageviews < overagePageviewIncrement

      if (isOverageLessThanOneIncrement) {
        // We don't bill for these small overages.
        return 0
      }

      if (this.isMeteredBillingPageviewsCustomerEnabled && this.hasPageviewsUnlimited) {
        return actualOveragePageviews
      }

      const isMeteredBillingLimitedWithExcess = this.isMeteredBillingPageviewsCustomerEnabled && this.totalPageviewsUsed >= this.totalPageviewsUsageLimit

      if (isMeteredBillingLimitedWithExcess) {
        // If metered billing is enabled, with a limit,
        // we cap billable overages at the max monthly limit set by the customer.
        //
        // Both the guard and the cap read the ONE computed. They used to read
        // two different sources — the guard `meteredBillingSettings`, the cap the
        // bare `meteredBilling` getter — so the branch could be entered on a
        // limit that was then read as `undefined`, and `undefined - number` put
        // NaN into the customer's estimated overage charge. Only one of the 15
        // components mixing this in even maps `meteredBilling`.
        return Math.max(this.totalPageviewsUsageLimit - this.pageviewsLimit, 0)
      }

      return actualOveragePageviews
    }
  },

  methods: {
    ...mapActions('kits', { fetchKitsFromStore: 'fetchKits' }),

    async fetchKits() {
      if (this.$account) {
        try {
          await this.fetchKitsFromStore()

          this.hasFetchedKits = true
        } catch (e) {
          this.kitsFetchFailed = true
        }
      }
    }
  }
}

export const DomainsMixin = {
  methods: {
    hasDomains(kit) {
      return kit.domains.indexOf('*.*') === -1
    },

    numberOfDomainsLabel(kit) {
      const num = kit.domains.length

      return `${num} ${num === 1 ? 'domain' : 'domains'}`
    }
  }
}
